import type { PlanGeoData } from '../proposition-c/types';
import { containsPoint, centerOf } from './geoTools';
import type { TechnicalPoint } from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseTechnicalPoints(text: string, fileName: string, geo: PlanGeoData): TechnicalPoint[] {
  const data: unknown = JSON.parse(text);
  if (!isRecord(data) || data.type !== 'FeatureCollection' || !Array.isArray(data.features) || !data.features.length) {
    throw new Error('GeoJSON attendu : FeatureCollection non vide de points WGS84 (longitude, latitude).');
  }
  if (data.crs) throw new Error('Reprojection non prise en charge. Exportez le fichier en WGS84 sans propriété crs.');
  const batch = crypto.randomUUID();
  return data.features.map((feature: unknown, index: number) => {
    if (!isRecord(feature) || feature.type !== 'Feature' || !isRecord(feature.geometry) || feature.geometry.type !== 'Point') {
      throw new Error(`Objet ${index + 1} : seuls les points GeoJSON sont acceptés.`);
    }
    const coordinates = feature.geometry.coordinates;
    if (!Array.isArray(coordinates) || coordinates.length < 2
      || !coordinates.every((value) => typeof value === 'number' && Number.isFinite(value))
      || Math.abs(coordinates[0]) > 180 || Math.abs(coordinates[1]) > 90) {
      throw new Error(`Point ${index + 1} : coordonnées WGS84 invalides.`);
    }
    const point: [number, number] = [coordinates[0], coordinates[1]];
    const buildings = geo.buildings.features.filter((building) => building.geometry && containsPoint(point, building.geometry));
    return {
      id: `${batch}-${index + 1}`, coordinates: point, status: 'pending', fileName,
      buildingId: buildings.length === 1 ? String(buildings[0].properties?.functionalId) : undefined,
    };
  });
}

export function pointObstacles(point: TechnicalPoint, all: TechnicalPoint[]): string[] {
  if (!point.buildingId) return ['Aucun bâtiment associé. Choisissez le bâtiment sur la carte ou dans la liste.'];
  const others = all.filter((item) => item.id !== point.id && item.buildingId === point.buildingId);
  const reasons: string[] = [];
  if (others.some((item) => item.status === 'pending')) reasons.push('Plusieurs points à vérifier sont associés à ce bâtiment : choisissez lequel retenir.');
  if (others.some((item) => item.status === 'validated')) reasons.push('Ce bâtiment possède déjà un local technique : remplacement à confirmer.');
  return reasons;
}

export function validatePoint(all: TechnicalPoint[], id: string, confirmed: boolean): TechnicalPoint[] {
  const point = all.find((item) => item.id === id);
  if (!point?.buildingId) throw new Error('Associez ce point à un bâtiment avant validation.');
  if (pointObstacles(point, all).length && !confirmed) throw new Error('Les conflits nécessitent une confirmation individuelle.');
  return all.map((item) => {
    if (item.id === id) return { ...item, status: 'validated' };
    if (item.buildingId === point.buildingId && item.status === 'validated') return { ...item, status: 'replaced' };
    if (item.buildingId === point.buildingId && item.status === 'pending') return { ...item, buildingId: undefined, status: 'pending' };
    return item;
  });
}

export function demoPoints(geo: PlanGeoData): TechnicalPoint[] {
  const buildings = geo.buildings.features.filter((feature) =>
    feature.geometry?.type === 'Polygon' || feature.geometry?.type === 'MultiPolygon').slice(0, 3);
  const base: TechnicalPoint[] = buildings.map((feature, index) => ({
    id: `demo-${crypto.randomUUID()}`, coordinates: centerOf(feature.geometry),
    buildingId: String(feature.properties?.functionalId), status: index === 0 ? 'validated' : 'pending',
    fileName: 'Démonstration : local existant et points importés',
  }));
  if (!base.length) throw new Error('Aucun bâtiment disponible pour la démonstration.');
  return [...base, ...base.slice(0, 2).map((point) => ({
    ...point, id: `demo-${crypto.randomUUID()}`, status: 'pending' as const,
    coordinates: [point.coordinates[0] + 0.00002, point.coordinates[1]] as [number, number],
  }))];
}
